import { useState, ChangeEvent, useEffect, useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import { FieldError, useForm } from 'react-hook-form';
import { validationDecoration, POSTCODE_REGEX } from '@zoopla/shared/forms';
import { DropdownWithLabel } from '@blueprint/dropdown';
import { TextField } from '@blueprint/text-input';
import { Label } from '@blueprint/ui';
import triggerTracking from '@blueprint/tracking';
import { SearchSmall } from '@zoopla-eng/zds-components/icons';

import {
    CallToActionRow,
    PropertyBlock,
    TextFieldWrapper,
    StyledButtonWithIcon,
    PostcodeHint
} from './GetHomeWorthEstimateForm.styles';
import { redirect } from './utils/redirect';
import { Address } from '../graphql.generated';
import { getTrackedPropertyTaxonomyData } from '@zoopla/analytics/property-data';
import { GetHomeWorthEstimateFormProps } from './GetHomeWorthEstimateForm.types';
import { QUERY_UPRN_PROPERTIES_IN_POSTCODE } from './queries/addressByPostcode.query';

const defaultLabels = {
    postcodeLabel: 'Enter full postcode, e.g. SW1 0RG',
    addressLabel: 'Address',
    findAddressButtonText: 'Look up postcode',
    getEstimateButtonText: 'Get an instant estimate'
};

const addressDropdownDefaultValue = 'Please select';
const noPostcodeError =
    "We do not recognise that postcode. Check it's for a UK address.";
const propertiesInPostcodeQueryError =
    'There was an error fetching properties for that postcode. Please try again.';
const invalidPostcodeValidationError =
    'Please provide a full, valid postcode e.g. AB1 2CD';

type AddressData = {
    addressesByPostcode: {
        addresses: Address[];
    };
};

export const formatPostcode = (postcode: string) => {
    const trimmedPostcode = postcode.trim();

    if (trimmedPostcode.length <= 3 || trimmedPostcode.indexOf(' ') > -1) {
        return trimmedPostcode;
    }

    const postcodeLength = trimmedPostcode.length;
    // the outcode is always 3 characters long
    const outcode = trimmedPostcode.slice(postcodeLength - 3, postcodeLength);
    const postcodeStart = trimmedPostcode.slice(0, postcodeLength - 3);

    return `${postcodeStart} ${outcode}`;
};

export const GetHomeWorthEstimateForm = ({
    eventCategory,
    idSuffix,
    labelId,
    initialPostcode = '',
    shouldFocusAfterInitialSearch = false,
    overriddenLabels,
    onSearchDone = () => null,
    onPdpNavigation = () => null
}: GetHomeWorthEstimateFormProps) => {
    const [properties, setProperties] = useState<Address[]>([]);
    const [isInitialSearchDone, setIsInitialSearchDone] = useState(
        !initialPostcode
    );
    const [selectedUPRN, setSelectedUPRN] = useState<string>('');
    const {
        handleSubmit,
        register,
        formState: { isSubmitting, errors },
        setError,
        clearErrors,
        setValue,
        getValues,
        getFieldState
    } = useForm({
        mode: 'onBlur'
    });

    const labels = { ...defaultLabels, ...overriddenLabels };

    const getPropertiesByPostcode = async (data: AddressData) => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: 'click',
            eventLabel: 'locate_address'
        });

        const propertiesFromApi = data.addressesByPostcode?.addresses;

        if (propertiesFromApi?.length) {
            setProperties(propertiesFromApi as Address[]);
            clearErrors();

            // we want to avoid focus on the dropdown in specific cases
            const shouldAvoidFocus =
                !isInitialSearchDone && !shouldFocusAfterInitialSearch;
            if (!shouldAvoidFocus) {
                setTimeout(() => {
                    // the blueprint DropdownWithLabel does not forward ref
                    // so we target it by using its id
                    document.getElementById('property-select')?.focus();
                }, 200);
            }
        } else {
            setProperties([]);
            setError('postcode', {
                type: 'manual',
                message: noPostcodeError
            });
        }

        setIsInitialSearchDone(true);
        onSearchDone();
    };

    const [getPostCodeWithUPRN, { loading: loadingUPRNProperties }] =
        useLazyQuery(QUERY_UPRN_PROPERTIES_IN_POSTCODE, {
            onError: function () {
                setProperties([]);
                setError('postcode', {
                    type: 'manual',
                    message: propertiesInPostcodeQueryError
                });
                return;
            },
            onCompleted: getPropertiesByPostcode,
            fetchPolicy: 'cache-and-network'
        });

    const redirectToPropertyDetailsPage = () => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: 'clickthrough',
            eventLabel: 'get_estimate',
            taxonomy: getTrackedPropertyTaxonomyData({ uprn: selectedUPRN })
        });

        const propertyDetailsPageRoute = `/property/uprn/${selectedUPRN}/`;
        redirect(propertyDetailsPageRoute);
    };

    const hasFetchedPropertiesByPostcode = properties && properties.length > 0;
    const hasEnteredValidPostcode =
        getValues('postcode') &&
        getValues('postcode') !== '' &&
        !errors?.['postcode'];
    const hasSelectedAddressFromDropdown = selectedUPRN;

    const buttonText = hasFetchedPropertiesByPostcode
        ? labels.getEstimateButtonText
        : labels.findAddressButtonText;

    const isButtonDisabled = hasFetchedPropertiesByPostcode
        ? !hasSelectedAddressFromDropdown
        : !hasEnteredValidPostcode;

    const handleChangePostcode = () => {
        setProperties([]);
        setSelectedUPRN('');
    };

    const handleChangeAddress = (event: ChangeEvent<HTMLSelectElement>) => {
        const {
            target: { value: uprn }
        } = event;

        setSelectedUPRN(uprn);
    };

    const formInputProps = (field: 'postcode') => ({
        id: field,
        name: field,
        validationErrorMessage: (errors[field] as FieldError)?.message,
        validationStatus: validationDecoration(getFieldState('postcode'))
    });

    const getPostcodeAddress = useCallback(() => {
        getPostCodeWithUPRN({
            variables: { postcode: formatPostcode(getValues('postcode')) }
        });
    }, [getPostCodeWithUPRN, getValues]);

    const onSubmit = () => {
        if (hasFetchedPropertiesByPostcode) {
            onPdpNavigation();
            redirectToPropertyDetailsPage();
            return;
        }

        getPostcodeAddress();
    };

    useEffect(() => {
        if (initialPostcode && initialPostcode !== '') {
            setIsInitialSearchDone(false);
            setValue('postcode', initialPostcode, { shouldValidate: true });
            getPostcodeAddress();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialPostcode]);

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            data-testid="home-estimate-form"
        >
            <TextFieldWrapper>
                {labels.postcodeCaption ? (
                    <Label
                        htmlFor={idSuffix ? `postcode-${idSuffix}` : 'postcode'}
                    >
                        {labels.postcodeCaption}
                    </Label>
                ) : null}
                <TextField
                    {...formInputProps('postcode')}
                    id={idSuffix ? `postcode-${idSuffix}` : 'postcode'}
                    label={labels.postcodeLabel}
                    data-testid={idSuffix ? `postcode-${idSuffix}` : 'postcode'}
                    defaultValue={initialPostcode}
                    aria-describedby={labelId}
                    {...register('postcode', {
                        required: invalidPostcodeValidationError,
                        pattern: {
                            value: POSTCODE_REGEX,
                            message: invalidPostcodeValidationError
                        },
                        onChange: handleChangePostcode
                    })}
                />
                {labels.postcodeHint && !errors?.['postcode'] ? (
                    <PostcodeHint>{labels.postcodeHint}</PostcodeHint>
                ) : null}
            </TextFieldWrapper>

            {hasFetchedPropertiesByPostcode ? (
                <PropertyBlock>
                    {labels.addressCaption ? (
                        <Label htmlFor="property-select">
                            {labels.addressCaption}
                        </Label>
                    ) : null}
                    <DropdownWithLabel
                        hasBorderRadius
                        id="property-select"
                        data-testid={
                            idSuffix
                                ? `property-select-${idSuffix}`
                                : 'property-select'
                        }
                        label={labels.addressLabel}
                        defaultValue={addressDropdownDefaultValue}
                        onChange={handleChangeAddress}
                        aria-describedby={labelId}
                        data-cs-mask
                    >
                        <option value={addressDropdownDefaultValue} disabled>
                            {addressDropdownDefaultValue}
                        </option>
                        {properties.map((property) => (
                            <option
                                value={property.uprn}
                                key={property.uprn}
                                label={property.fullAddress}
                            >
                                {property.fullAddress}
                            </option>
                        ))}
                    </DropdownWithLabel>
                </PropertyBlock>
            ) : null}
            <CallToActionRow>
                <StyledButtonWithIcon
                    variant="primary"
                    data-testid={
                        idSuffix
                            ? `estimate-button-${idSuffix}`
                            : 'estimate-button'
                    }
                    disabled={isButtonDisabled}
                    isLoading={loadingUPRNProperties || isSubmitting}
                >
                    <SearchSmall />
                    {buttonText}
                </StyledButtonWithIcon>
            </CallToActionRow>
        </form>
    );
};

export default GetHomeWorthEstimateForm;
